import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Upload, AlertTriangle, Loader2, X } from "lucide-react";
import api from "../lib/api";
import { Attribution } from "../components/Attribution";

export default function Identify() {
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const inputRef = useRef();

  const pick = (f) => {
    if (!f) return;
    setFile(f); setResult(null); setError("");
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/identify", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
    } catch (e) {
      setError("Identification failed. Try a clearer, single-chair photo.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setPreview(null); setFile(null); setResult(null); setError(""); };

  return (
    <div className="max-w-[1000px] mx-auto px-5 sm:px-8 pt-10">
      <p className="overline text-accent">Experimental</p>
      <h1 className="serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-1">Identify a Chair</h1>
      <p className="text-muted-foreground mt-4 max-w-xl">Upload a photo of a chair you've spotted. Our AI compares it to the catalog and returns the three closest matches with a confidence score.</p>

      <div className="flex items-start gap-3 border border-accent/30 bg-accent/5 p-4 mt-6" data-testid="identify-disclaimer">
        <AlertTriangle size={18} className="text-accent shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground">This is a <strong className="text-foreground">best-guess</strong> tool. Replicas, knockoffs and lookalikes are common in furniture and will affect accuracy. Always confirm against the source institution.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 mt-10">
        <div>
          {!preview ? (
            <button
              onClick={() => inputRef.current.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }}
              className="w-full aspect-[4/3] border-2 border-dashed border-border hover:border-accent bg-card flex flex-col items-center justify-center gap-3 transition-colors"
              data-testid="upload-dropzone"
            >
              <Upload size={28} className="text-muted-foreground" />
              <span className="text-sm font-medium">Click or drag a photo here</span>
              <span className="text-xs text-muted-foreground">JPEG, PNG or WEBP</span>
            </button>
          ) : (
            <div className="relative">
              <img src={preview} alt="upload preview" className="w-full aspect-[4/3] object-cover border border-border" data-testid="upload-preview" />
              <button onClick={reset} className="absolute top-2 right-2 bg-background/90 p-2 border border-border" data-testid="reset-upload"><X size={16} /></button>
            </div>
          )}
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid="file-input" />
          <button
            onClick={submit}
            disabled={!file || loading}
            className="w-full bg-accent text-accent-foreground h-12 mt-4 text-sm font-medium disabled:opacity-40 inline-flex items-center justify-center gap-2"
            data-testid="identify-submit"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Analysing…</> : "Identify this chair"}
          </button>
          {error && <p className="text-sm text-destructive mt-3" data-testid="identify-error">{error}</p>}
        </div>

        <div>
          {!result && !loading && <p className="text-sm text-muted-foreground">Your top three matches will appear here.</p>}
          {loading && <p className="text-sm text-muted-foreground">Comparing against the catalog…</p>}
          {result && (
            <div className="space-y-5" data-testid="identify-results">
              <p className="overline">Top matches</p>
              {result.matches.length === 0 && <p className="text-sm text-muted-foreground">No confident matches found. Try another angle.</p>}
              {result.matches.map((m, i) => (
                <Link key={m.chair.slug} to={`/chair/${m.chair.slug}`} className="flex gap-4 border border-border bg-card p-3 hover:border-accent transition-colors group" data-testid={`match-${i}`}>
                  <img src={m.chair.image_url} alt={m.chair.title} className="w-20 h-24 object-cover shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-accent">{m.confidence}% match</span>
                    </div>
                    <h3 className="serif text-lg leading-tight mt-0.5 group-hover:text-accent transition-colors">{m.chair.title}</h3>
                    <p className="text-xs text-muted-foreground">{m.chair.designer} · {m.chair.year}</p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{m.reason}</p>
                    <Attribution chair={m.chair} className="mt-1" linked={false} />
                  </div>
                </Link>
              ))}
              <p className="text-xs text-muted-foreground italic">{result.disclaimer}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
